import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginForm from "./LoginForm";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, setDoc, getDoc, onSnapshot } from "firebase/firestore";
import PublicView from "./PublicView";
import AdminQR from "./AdminQR";
import Unlock from "./Unlock";
import "./theme.css";

const ADMIN_EMAIL = "susannahldorrance@gmail.com";

function App() {
  const [user, setUser] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [childName, setChildName] = useState("");
  const [publicToken, setPublicToken] = useState("");

  // 🔑 Generate QR token
  function generateToken() {
    return Math.random().toString(36).substring(2, 10);
  }

  // 🔐 Auth + Firestore sync
useEffect(() => {
  let unsubscribeDoc = null;

  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    console.log("onAuthStateChanged triggered");

    if (user) {
      console.log("Logged in");

      const docRef = doc(db, "parents", user.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.log("Creating new parent doc");

        const newData = {
          parentName: "",
          parentEmail: user.email,
          parentPhone: "",
          childName: "",
          publicToken: generateToken(),
          hasAccess: false,
          createdAt: new Date().toISOString(),
        };

        await setDoc(docRef, newData);
      }

      // 🔥 REAL-TIME LISTENER (THIS IS THE FIX)
      unsubscribeDoc = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          console.log("🔥 realtime update:", data);

          setHasAccess(data.hasAccess || false);
          setParentName(data.parentName || "");
          setParentEmail(data.parentEmail || user.email || "");
          setParentPhone(data.parentPhone || "");
          setChildName(data.childName || "");
          setPublicToken(data.publicToken || "");
        }
      });

      setUser(user);
    } else {
      console.log("Logged out");
      setUser(null);
      setHasAccess(false);
    }

    setAuthChecked(true);
  });

  return () => {
    unsubscribe();
    if (unsubscribeDoc) unsubscribeDoc();
  };

// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // ⏳ Prevent rendering before auth is ready
  if (!authChecked) {
    return (
      <div className="portal-page">
        <img
          src={`${process.env.PUBLIC_URL}/pink-collective-logo.png`}
          alt="logo"
          className="portal-logo"
        />
        <div className="portal-card">
          <h1 className="portal-title">Loading</h1>
          <p className="portal-text">Checking login status...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>

        <Route path="/" element={<Home />} />

        {/* 🔐 EDIT PAGE */}
        <Route
          path="/edit"
          element={
            !user ? (
              <LoginForm onLogin={() => {}} />
            ) : !hasAccess ? (
              <Navigate to="/unlock" />
            ) : (
              <div className="portal-page">
                <img
                  src={`${process.env.PUBLIC_URL}/pink-collective-logo.png`}
                  alt="logo"
                  className="portal-logo"
                />

                <button
                  className="portal-button portal-button-secondary"
                  onClick={() => signOut(auth)}
                >
                  Logout
                </button>

                <Edit
                  user={user}
                  parentName={parentName}
                  parentEmail={parentEmail}
                  parentPhone={parentPhone}
                  childName={childName}
                  publicToken={publicToken}
                  setParentName={setParentName}
                  setParentEmail={setParentEmail}
                  setParentPhone={setParentPhone}
                  setChildName={setChildName}
                />
              </div>
            )
          }
        />

        {/* 🔓 UNLOCK */}
        <Route path="/unlock" element={<Unlock />} />

        {/* 🌐 PUBLIC QR */}
        <Route path="/p/:token" element={<PublicView />} />

        {/* 🛠 ADMIN QR */}
        <Route
          path="/admin-qr"
          element={
            user && user.email === ADMIN_EMAIL ? (
              <AdminQR />
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
  path="/login"
  element={<LoginForm onLogin={() => {}} />}
/>

        {/* 🛠 ADMIN HOME */}
        <Route
          path="/admin"
          element={
            user ? (
              <div className="portal-page">
                <img
                  src={`${process.env.PUBLIC_URL}/pink-collective-logo.png`}
                  alt="logo"
                  className="portal-logo"
                />

                <button
                  className="portal-button portal-button-secondary"
                  onClick={() => signOut(auth)}
                >
                  Logout
                </button>

                <div className="portal-card">
                  <h1 className="portal-title">Admin Home</h1>
                  <p className="portal-text">You are logged in.</p>

                  <a href="/admin-qr" className="portal-button">
                    Go to QR Generator
                  </a>
                </div>
              </div>
            ) : (
              <LoginForm onLogin={() => {}} />
            )
          }
        />

      </Routes>
    </BrowserRouter>
  );
}

function Home() {
  return (
    <div className="portal-page">
      <img
        src={`${process.env.PUBLIC_URL}/pink-collective-logo.png`}
        alt="logo"
        className="portal-logo"
      />

      <div className="portal-card">
        <h1 className="portal-title">Parent Portal</h1>

        <p className="portal-text">
          <a
            href="/login"
            style={{ textDecoration: "underline", fontWeight: "bold" }}
          >
            Log in
          </a>{" "}
          to view or edit your information.
        </p>

        <a
          href="https://thepinkcollective.org/parents"
          className="portal-button portal-button-secondary"
          style={{
            textDecoration: "none",
            display: "block",
            width: "fit-content",
            margin: "20px auto 0",
          }}
        >
          Return to Safrly
        </a>
      </div>
    </div>
  );
}

function Edit({
  user,
  parentName,
  parentEmail,
  parentPhone,
  childName,
  publicToken,
  setParentName,
  setParentEmail,
  setParentPhone,
  setChildName,
}) {
const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    if (!publicToken) {
      alert("This profile does not have a QR token.");
      return;
    }

    // Save the complete parent record
    await setDoc(
      doc(db, "parents", user.uid),
      {
        parentName,
        parentEmail,
        parentPhone,
        childName,
      },
      { merge: true }
    );

    // Save ONLY the information intended to appear publicly
    await setDoc(
      doc(db, "publicProfiles", publicToken),
      {
        ownerUid: user.uid,
        parentName,
        parentEmail,
        parentPhone,
        childName,
      },
      { merge: true }
    );

    alert("Saved!");
  } catch (err) {
    console.error("Error saving profile:", err);
    alert(`Error saving: ${err.message}`);
  }
};

  return (
    <div className="portal-card">
      <h1 className="portal-title">Edit Parent Info</h1>

      <form onSubmit={handleSubmit} className="portal-form">
        <input
          className="portal-input"
          placeholder="Parent Name"
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
        />

        <input
          className="portal-input"
          placeholder="Parent Email"
          value={parentEmail}
          onChange={(e) => setParentEmail(e.target.value)}
        />

        <input
          className="portal-input"
          placeholder="Parent Phone"
          value={parentPhone}
          onChange={(e) => setParentPhone(e.target.value)}
        />

        <input
          className="portal-input"
          placeholder="Child Name"
          value={childName}
          onChange={(e) => setChildName(e.target.value)}
        />

        <button className="portal-button">Save Info</button>
      </form>
    </div>
  );
}

export default App;